// src/components/HeaderComponent.js
import React, { useState, useEffect, useRef } from 'react';
import socket from '../socket';
import { Navbar, Nav, Container, Button, Dropdown, Offcanvas } from 'react-bootstrap';
import { useNavigate, Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePWA } from '../context/PWAContext'; 
import { useTheme } from '../context/ThemeContext'; 
import NotificationBell from './NotificationBell';
import { fetchNotificationsApi } from '../services/api';
import './css/MobileBottomNav.css';

import { Menu, X, ChevronDown, Bell, LogIn, UserPlus, Phone, Briefcase, Star, Search, UserCircle, Download, Sun, Moon, Globe, Home, ChartBarDecreasingIcon, LayoutGrid, Workflow, PlayCircle, Users, Settings, LogOut, ChevronRight, ChevronLeft, MessageSquare, LayoutDashboard, CreditCard } from 'lucide-react';
import { FaWhatsapp, FaVideo } from 'react-icons/fa';
import './css/HeaderComponent.css';
import logoBlackWhite from '../assets/img/logo-white-red.png';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const HeaderComponent = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false); // ✅ PROFILE MENU BAR STATE (FOR TALENT & DIRECTOR)
  const navigate = useNavigate();
  const location = useLocation(); 
  const { isAuthenticated, user, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const notificationCount = notifications.length;
  const navRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  const [language, setLanguage] = useState('EN');

  // ✅ PWA STATE
  const { isStandalone, isDismissed, promptInstall } = usePWA();
  const showInstallHeaderBtn = isDismissed && !isStandalone;

  const isActive = (path) => {
    return location.pathname === path || location.pathname.includes(path);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ AUTO-SHOW SCROLL BAR WHEN ON DASHBOARD
  useEffect(() => {
    if (location.pathname.includes('/dashboard')) {
      setShowProfileMenu(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNotifications();
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    try {
      const res = await fetchNotificationsApi();
      if (res.data?.success) {
        setNotifications(res.data.data || []);
      }
    } catch (err) {
      console.error("Notification fetch failed:", err.message);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    socket.connect();
    socket.emit("join", user.id);

    socket.on("new_notification", (notification) => {
      console.log("🔔 New Notification Received", notification);
      setNotifications(prev => [
        notification,
        ...prev
      ]);
    });

    return () => {
      socket.off("new_notification");
      socket.disconnect();
    };
  }, [isAuthenticated, user]);

  const handleClose = () => setShowDrawer(false);
  const handleShow = () => setShowDrawer(true);

  const handleLogout = () => {
    logout();
    handleClose();
    setShowProfileMenu(false);
    navigate('/login');
  };

  // ✅ SINGLE CLICK PROFILE HANDLER (TALENT & DIRECTOR BOTH)
  const handleProfileClick = (e) => {
    if (e) e.preventDefault();
    setShowProfileMenu(true);
    if (!location.pathname.includes('/dashboard')) {
      navigate('/dashboard/overview');
    }
  };

  const renderDashboardLinks = (isMobile = false) => {
    const roleCode = String(user?.roleCode);
    const clickHandler = isMobile ? handleClose : undefined;

    const TalentLinks = (
      <>  
        <Nav.Link as={Link} to="/" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('recommended') ? 'active-tab-box' : ''}`}><Home size={18} /> Home</Nav.Link>
        <Nav.Link as={Link} to="/roles" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('ongoing') ? 'active-link-box' : ''}`}><PlayCircle size={18} /> Browse Roles</Nav.Link>
        <Nav.Link as={Link} to="/how-it-works" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('how-it-works') ? 'active-tab-box' : ''}`}><Workflow size={18} /> How It Works</Nav.Link>
        <Nav.Link as={Link} to="/dashboard/overview" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('overview') ? 'active-tab-box' : ''}`}><LayoutDashboard size={18} /> Dashboard</Nav.Link>
      </>
    );

    const DirectorLinks = (
      <>
        <Nav.Link as={Link} to="/" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('recommended') ? 'active-tab-box' : ''}`}><Home size={18} /> Home</Nav.Link>
        <Nav.Link as={Link} to="/roles" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('ongoing') ? 'active-link-box' : ''}`}><ChartBarDecreasingIcon size={18} /> Browse Roles</Nav.Link>
        <Nav.Link as={Link} to="/how-it-works" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('my-auditions') ? 'active-tab-box' : ''}`}><Workflow size={18} /> How It Works</Nav.Link>
        <Nav.Link as={Link} to="/dashboard/overview" onClick={clickHandler} className={`nav-item-custom ${location.pathname.includes('overview') ? 'active-tab-box' : ''}`}><LayoutGrid size={18} /> Overview</Nav.Link>
      </>
    );

    return roleCode === '300' ? TalentLinks : DirectorLinks;
  };

  const renderPublicLinks = (isMobile = false) => {
    const baseClass = isMobile ? 'bottom-nav-item' : 'nav-item-custom';
    const activeClass = isMobile ? 'active-bottom-shadow' : 'active-tab-box';

    return (
      <>
        <Nav.Link as={Link} to="/" className={`${baseClass} ${(location.pathname.includes('HomePage') || location.pathname === '/') ? activeClass : ''}`}>
          <Home size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">Home</span> : "Home"}
        </Nav.Link>
        <Nav.Link as={Link} to="/roles" className={`${baseClass} ${location.pathname.includes('roles') ? activeClass : ''}`}>
          <PlayCircle size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">Auditions</span> : "Auditions"}
        </Nav.Link>
        <Nav.Link as={Link} to="/workshops" className={`${baseClass} ${location.pathname.includes('workshops') ? activeClass : ''}`}>
          <Briefcase size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">Workshops</span> : "Workshops"}
        </Nav.Link>
        <Nav.Link as={Link} to="/how-it-works" className={`${baseClass} ${location.pathname.includes('how-it-works') ? activeClass : ''}`}>
          <Workflow size={isMobile ? 22 : 18} /> {isMobile ? <span className="nav-label">How It Works</span> : "How It Works"}
        </Nav.Link>
      </>
    );
  };

  const renderBottomNavbar = () => {
    const clickHandler = handleClose;
    if (!isMobile) return null;

    return (
      <nav className="mobile-bottom-nav">
        <NavLink 
          to="/" 
          onClick={() => { clickHandler(); setShowProfileMenu(false); }} 
          className={({ isActive }) => (isActive || location.pathname === '/' ? "mobile-nav-item active" : "mobile-nav-item")} 
          end
        >
          <Home size={22} className="mobile-nav-icon" />
          <span>Home</span>
        </NavLink>

        <NavLink 
          to="/roles" 
          onClick={() => { clickHandler(); setShowProfileMenu(false); }} 
          className={({ isActive }) => (isActive ? "mobile-nav-item active" : "mobile-nav-item")}
        >
          <Briefcase size={22} className="mobile-nav-icon" />
          <span>Auditions</span>
        </NavLink>

        <NavLink 
          to="/how-it-works" 
          onClick={() => { clickHandler(); setShowProfileMenu(false); }} 
          className={({ isActive }) => (isActive ? "mobile-nav-item active" : "mobile-nav-item")}
        >
          <Star size={22} className="mobile-nav-icon" />
          <span>How It Works</span>
        </NavLink>

        <NavLink 
          to="/workshops" 
          onClick={() => { clickHandler(); setShowProfileMenu(false); }} 
          className={({ isActive }) => (isActive ? "mobile-nav-item active" : "mobile-nav-item")}
        >
          <Briefcase size={22} className="mobile-nav-icon" />
          <span>Workshops</span>
        </NavLink>
        
        {/* ✅ PROFILE CLICK FOR BOTH TALENT AND DIRECTOR */}
        {isAuthenticated ? (
          <button
            type="button"
            onClick={handleProfileClick}
            className={`mobile-nav-item ${showProfileMenu || location.pathname.includes('dashboard') ? 'active' : ''}`}
            style={{ background: 'transparent', border: 'none' }}
          >
            <UserCircle size={22} className="mobile-nav-icon" />
            <span>Profile</span>
          </button>
        ) : (
          <NavLink to="/login" onClick={clickHandler} className={({ isActive }) => (isActive ? "mobile-nav-item active" : "mobile-nav-item")}>
            <UserCircle size={22} className="mobile-nav-icon" />
            <span>Profile</span>
          </NavLink>
        )}
      </nav>
    );
  };

  return (
    <>
      <Navbar fixed="top" expand="lg" className="main-header">
        <Container fluid className="px-lg-5">
          
          <Navbar.Brand as={Link} to="/" className="brand-logo d-flex align-items-center">
            <img src={logoBlackWhite} alt="BookMyAudition" />
          </Navbar.Brand>

          {/* --- DESKTOP NAVIGATION --- */}
          <div className="d-none d-lg-flex w-100 justify-content-between align-items-center">
            <Nav className="m-auto header-nav-links justify-content-center">
              {isAuthenticated ? renderDashboardLinks(false) : renderPublicLinks(false)}
            </Nav>

            <div className="d-flex align-items-center gap-2">
              {showInstallHeaderBtn && (
                <button 
                  onClick={promptInstall}
                  className="header-icon-btn" 
                  title="Install App"
                >
                  <Download size={18} />
                </button>
              )}

              {/* Language Switcher */}
              <Dropdown align="end">
                <Dropdown.Toggle as="button" className="header-icon-btn no-caret" title="Switch Language" style={{ width: 'auto', padding: '0 12px', borderRadius: '20px', display: 'flex', gap: '6px' }}>
                  <Globe size={18} /> <span style={{fontSize: '12px', fontWeight: 'bold'}}>{language}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow-lg border-0 mt-2">
                  <Dropdown.Item onClick={() => setLanguage('EN')} className={language === 'EN' ? 'fw-bold' : ''}>English (EN)</Dropdown.Item>
                  <Dropdown.Item onClick={() => setLanguage('HI')} className={language === 'HI' ? 'fw-bold' : ''}>Hindi (HI)</Dropdown.Item>
                  <Dropdown.Item onClick={() => setLanguage('MR')} className={language === 'MR' ? 'fw-bold' : ''}>Marathi (MR)</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {isAuthenticated && (
                <div style={{ position: "relative" }}>
                  <NotificationBell count={notificationCount} onClick={() => setShowNotif(!showNotif)} />
                </div>
              )}

              <a href="tel:8795023799" className="header-icon-btn" title="Call"><Phone size={18} /></a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className="header-icon-btn" title="WhatsApp"><FaWhatsapp size={18} /></a>

              <div className="auth-section d-flex align-items-center">
                {isAuthenticated ? (
                  <Dropdown align="end">
                    <Dropdown.Toggle variant="link" className="profile-dropdown-btn p-0 no-caret"><UserCircle size={32} /></Dropdown.Toggle>
                    <Dropdown.Menu className="shadow-lg border-0 mt-2">
                      <Dropdown.Item as={Link} to="/dashboard/profile">My Profile</Dropdown.Item>
                      <Dropdown.Item as={Link} to="/dashboard/ChangePasswordPage">Settings</Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleLogout} className="text-danger">Sign Out</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <div className="d-flex gap-2 ms-2">
                    <Link to="/login" className="header-icon-btn" title="Login"><LogIn size={20} /></Link>
                    <Link to="/register" className="header-icon-btn" title="Sign Up"><UserPlus size={20} /></Link>
                  </div>
                )}
              </div>
            </div>
          </div> 
            
          {/* --- MOBILE NAVIGATION --- */}
          {isMobile ? (
            <div className="mobile-header-actions ms-auto d-flex align-items-center gap-1">

              {/* PWA MOBILE INSTALL BUTTON */}
              {showInstallHeaderBtn && (
                <button 
                  onClick={promptInstall} 
                  className="mobile-icon-btn me-1" 
                  style={{ backgroundColor: '#c30000', color: '#f9f9f9', border: 'none' }}
                  title="Install App"
                >
                  <Download size={20} />
                </button>
              )}

              <Link to="/roles" className="mobile-icon-btn" title="Search Roles"><Search size={20} /></Link>
              <a href="tel:8795023799" className="mobile-icon-btn" title="Call Us"><Phone size={20} /></a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className="mobile-icon-btn" title="WhatsApp"><FaWhatsapp size={20} /></a>

              {isAuthenticated && (
                <div style={{ position: "relative" }}>
                  <NotificationBell
                    count={notificationCount}
                    onClick={() => setShowNotif(!showNotif)}
                  />
                </div>
              )}

              {/* Language Dropdown */}
              <Dropdown align="end" className="d-flex align-items-center justify-content-center">
                <Dropdown.Toggle variant="link" className="mobile-icon-btn p-0 no-caret border-0 bg-transparent" title="Switch Language">
                  <Globe size={20} />
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow-lg border-0 mt-2">
                  <Dropdown.Item onClick={() => setLanguage('EN')} className={language === 'EN' ? 'fw-bold' : ''}>EN</Dropdown.Item>
                  <Dropdown.Item onClick={() => setLanguage('HI')} className={language === 'HI' ? 'fw-bold' : ''}>HI</Dropdown.Item>
                  <Dropdown.Item onClick={() => setLanguage('MR')} className={language === 'MR' ? 'fw-bold' : ''}>MR</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* LOGIN / SIGN UP / PROFILE / LOGOUT ICONS */}
              {!isAuthenticated ? (
                <>
                  <Link 
                    to="/login" 
                    className={`mobile-icon-btn ${isActive('/login') ? 'mobile-icon-active' : ''}`}
                    title="Login"
                  >
                    <LogIn size={20} />
                  </Link>
                  <Link 
                    to="/register" 
                    className={`mobile-icon-btn ${isActive('/register') ? 'mobile-icon-active' : ''}`}
                    title="Sign Up"
                  >
                    <UserPlus size={20} />
                  </Link>
                </>
              ) : (
                <>
                  {/* <button 
                    onClick={handleProfileClick} 
                    className={`mobile-icon-btn ${isActive('/dashboard') || showProfileMenu ? 'mobile-icon-active' : ''}`}
                    style={{ border: 'none', background: 'transparent' }}
                    title="Profile Menu"
                  >
                    <UserCircle size={20} />
                  </button> */}

                  <button 
                    onClick={handleLogout} 
                    className="mobile-icon-btn " 
                    style={{ border: 'none', background: 'transparent' }}
                    title="Logout"
                  >
                    <LogOut size={20} />
                  </button>
                </>
              )}

            </div>
          ) : (
            <button onClick={handleShow} className="d-none border-0 bg-transparent p-2 ms-auto">
              <Menu size={28} color="#334155" />
            </button>
          )}

          <Offcanvas show={showDrawer} onHide={handleClose} placement="end" className="mobile-drawer">
            <Offcanvas.Header className="border-bottom" closeButton>
               <h5 className="mb-0 fw-bold brand-text text-white">BookMyAudition</h5>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-3 d-flex flex-column">
              {isAuthenticated && (
                <div className="mobile-user-info mb-4 d-flex align-items-center gap-3 p-3 bg-light-transparent rounded-3">
                  <UserCircle size={40} className="text-white" />
                  <div>
                    <h6 className="mb-0 fw-bold text-white">Welcome</h6>
                    <small className="text-white-50">Manage your account</small>
                  </div>
                </div>
              )}
              <Nav className="flex-column mobile-nav-links gap-2">
                 {isAuthenticated ? renderDashboardLinks(true) : renderPublicLinks(true)}
              </Nav>
              <hr className="my-4 border-white-50"/>
              <div className="mt-auto">
                {isAuthenticated ? (
                  <Nav className="flex-column gap-2">
                     <Nav.Link as={Link} to="/dashboard/profile" onClick={handleClose} className="nav-item-custom"><UserCircle size={18} /> My Profile</Nav.Link>
                     <Nav.Link as={Link} to="/dashboard/ChangePasswordPage" onClick={handleClose} className="nav-item-custom"><Settings size={18} /> Settings</Nav.Link>
                     <Button variant="outline-light" onClick={handleLogout} className="w-100 mt-3 d-flex align-items-center justify-content-center gap-2"><LogOut size={18} /> Sign Out</Button>
                  </Nav>
                ) : (
                  <div className="d-grid gap-3">
                    <Link to="/login" onClick={handleClose}><Button variant="outline-light" className="w-100">Login</Button></Link>
                    <Link to="/register" onClick={handleClose}><Button variant="light" className="w-100">Sign Up</Button></Link>
                  </div>
                )}
              </div>
            </Offcanvas.Body>
          </Offcanvas>

        </Container>
      </Navbar>

      {/* ✅ ROLE-BASED HORIZONTAL SCROLL BAR (TALENT vs DIRECTOR) */}
      {isMobile && isAuthenticated && (showProfileMenu || location.pathname.includes('/dashboard')) && (
        <div className="bma-cd-profile-bar-wrapper">
          <div className="bma-cd-profile-bar-scroll">
            {String(user?.roleCode) === '300' ? (
              /* --- TALENT MENU ITEMS --- */
              <>
                <Link 
                  to="/dashboard/overview" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('overview') || location.pathname === '/dashboard' ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <LayoutDashboard size={16} /> <span>Dashboard</span>
                </Link>

                <Link 
                  to="/dashboard/profile" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('profile') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <UserCircle size={16} /> <span>Profile</span>
                </Link>

                <Link 
                  to="/dashboard/my-audition" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('my-audition') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <Briefcase size={16} /> <span>My Application</span>
                </Link>

                <Link 
                  to="/dashboard/meetings" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('meetings') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <FaVideo size={16} /> <span>Meetings</span>
                </Link>

                <Link 
                  to="/dashboard/platform-feedback" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('platform-feedback') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <MessageSquare size={16} /> <span>Feedback</span>
                </Link>

                <Link 
                  to="/dashboard/billing" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('billing') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <CreditCard size={16} /> <span>Billing</span>
                </Link>

                <button onClick={handleLogout} className="bma-cd-profile-bar-item bma-cd-profile-bar-item-danger">
                  <LogOut size={16} /> <span>Sign Out</span>
                </button>
              </>
            ) : (
              /* --- DIRECTOR / CASTING DIRECTOR MENU ITEMS --- */
              <>
                <Link 
                  to="/dashboard/overview" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('overview') || location.pathname === '/dashboard' ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <LayoutDashboard size={16} /> <span>Dashboard</span>
                </Link>

                <Link 
                  to="/dashboard/profile" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('profile') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <UserCircle size={16} /> <span>Profile</span>
                </Link>

                <Link 
                  to="/dashboard/projects" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('projects') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <Briefcase size={16} /> <span>Projects</span>
                </Link>

                <Link 
                  to="/dashboard/roles" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('roles') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <Users size={16} /> <span>Manage Roles</span>
                </Link>

                <Link 
                  to="/dashboard/manage-applications" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('manage-applications') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <Users size={16} /> <span>Audition</span>
                </Link>

                <Link 
                  to="/dashboard/meetings" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('meetings') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <FaVideo size={16} /> <span>Meetings</span>
                </Link>

                <Link 
                  to="/dashboard/team-members" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('team-members') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <Users size={16} /> <span>Team Members</span>
                </Link>

                <Link 
                  to={String(user?.roleCode) === '200' ? "/dashboard/director-testimonials" : "/dashboard/casting-testimonials"} 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('testimonials') || location.pathname.includes('reviews') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <Star size={16} /> <span>Reviews</span>
                </Link>

                <Link 
                  to="/dashboard/transaction-history" 
                  className={`bma-cd-profile-bar-item ${location.pathname.includes('transaction-history') ? 'bma-cd-profile-bar-item-active' : ''}`}
                >
                  <CreditCard size={16} /> <span>Billing</span>
                </Link>

                <button onClick={handleLogout} className="bma-cd-profile-bar-item bma-cd-profile-bar-item-danger">
                  <LogOut size={16} /> <span>Sign Out</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showNotif && (
        <div className="notif-popup">
          <div className="notif-header">
            <div className="notif-header-left">
              <div className="notif-header-icon">🔔</div>
              <div>
                <div className="notif-title">Notifications</div>
                <div className="notif-subtitle">{notifications.length} new updates</div>
              </div>
            </div>
            <button onClick={() => setShowNotif(false)} className="notif-close-btn">✕</button>
          </div>

          <div className={`notif-list ${notifications.length > 3 ? "notif-scroll" : ""}`}>
            {notifications.length === 0 ? (
              <div className="notif-empty">No notifications</div>
            ) : (
              notifications.map((n, i) => (
                <div
                  key={i}
                  onClick={() => {
                    if (n.redirect_url) {
                      setShowNotif(false);
                      navigate(n.redirect_url);
                    }
                  }}
                  className="notif-item"
                >
                  <div className="notif-item-inner">
                    <div className="notif-item-icon">🎭</div>
                    <div className="notif-content">
                      <div className="notif-item-title">{n.title}</div>
                      <div className="notif-item-message">{n.message}</div>
                      <div className="notif-item-time">
                        {n.created_at ? dayjs(n.created_at).format("DD MMM YYYY hh:mm A") : "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {renderBottomNavbar()} 
    </>
  );
};

export default HeaderComponent;